<template>
  <div class="rsfl-page">
    <div class="rsfl-filters">
      <div class="rsfl-filters__inner">
        <v-autocomplete
          v-model="form.partner_id"
          :items="partners"
          item-text="name"
          item-value="id"
          label="Partner"
          outlined
          dense
          hide-details
          clearable
          :loading="loadingPartners"
          class="rsfl-filters__field"
          @keyup.enter="search"
        />

        <v-select
          v-model="form.status"
          :items="statusOptions"
          label="Status"
          outlined
          dense
          hide-details
          class="rsfl-filters__field"
        />

        <v-btn
          class="accent white--text"
          :disabled="!form.partner_id"
          :loading="loading"
          @click="search"
        >
          Search
        </v-btn>
      </div>
    </div>

    <!-- Nothing searched yet -->
    <v-card v-if="!applied.partner_id" outlined class="rsfl-state rsfl-state--card">
      <v-icon size="44" color="grey lighten-1">mdi-account-search-outline</v-icon>
      <h3 class="rsfl-state__title">Select a partner</h3>
      <p class="rsfl-state__text">
        Pick a partner above and hit Search to see their fortnightly revenue splits.
      </p>
    </v-card>

    <!-- Loading -->
    <div v-else-if="loading" class="rsfl-state">
      <v-progress-circular indeterminate color="primary" size="40" width="3" />
      <p class="rsfl-state__text">Loading revenue splits…</p>
    </div>

    <!-- Content -->
    <template v-else-if="periods.length">
      <!-- Totals -->
      <div class="rsfl-totals">
        <div class="rsfl-total">
          <span class="rsfl-total__label">Total Revenue</span>
          <span class="rsfl-total__value">{{ currency(totals.revenue) }}</span>
        </div>
        <div class="rsfl-total">
          <span class="rsfl-total__label">Already Paid Out</span>
          <span class="rsfl-total__value rsfl-total__value--success">
            {{ currency(totals.settled) }}
          </span>
        </div>
        <div class="rsfl-total">
          <span class="rsfl-total__label">Awaiting Transaction Details</span>
          <span class="rsfl-total__value rsfl-total__value--warning">
            {{ currency(totals.pending) }}
          </span>
        </div>
        <div class="rsfl-total">
          <span class="rsfl-total__label">Pending Periods</span>
          <span class="rsfl-total__value">{{ totals.pendingCount }}</span>
        </div>
      </div>

      <!-- Fortnight table -->
      <div class="rsfl-details-section">
        <h3 class="rsfl-details-title">
          Fortnight Records
          <span class="rsfl-details-title__count">
            ({{ visiblePeriods.length }} of {{ periods.length }})
          </span>
        </h3>

        <v-card v-if="!visiblePeriods.length" outlined class="rsfl-state rsfl-state--card">
          <v-icon size="36" color="grey lighten-1">mdi-filter-off-outline</v-icon>
          <p class="rsfl-state__text">No fortnights match this status filter.</p>
        </v-card>

        <v-data-table
          v-else
          :headers="tableHeaders"
          :items="periodsWithKey"
          item-key="_key"
          :expanded.sync="expandedItems"
          show-expand
          dense
          hide-default-footer
          :items-per-page="-1"
          class="rsfl-data-table"
        >
          <!-- Period Column -->
          <template v-slot:item.period="{ item }">
            <span class="rsfl-row__period">{{ periodTitle(item) }}</span>
          </template>

          <!-- Share % Column -->
          <template v-slot:item.share_percentage="{ item }">
            {{ percentOrDash(item.share_percentage) }}
          </template>

          <!-- Base Amount Column -->
          <template v-slot:item.base_amount="{ item }">
            {{ currencyOrDash(item.base_amount) }}
          </template>

          <!-- GST % Column -->
          <template v-slot:item.gst_percentage="{ item }">
            {{ percentOrDash(item.gst_percentage) }}
          </template>

          <!-- GST Amount Column -->
          <template v-slot:item.gst_amount="{ item }">
            {{ currencyOrDash(item.gst_amount) }}
          </template>

          <!-- GST Type Column -->
          <template v-slot:item.gst_type="{ item }">
            {{ item.gst_type || "—" }}
          </template>

          <!-- Total Column -->
          <template v-slot:item.total_amount="{ item }">
            <span class="rsfl-td--strong">{{ currency(item.total_amount) }}</span>
          </template>

          <!-- Status Column -->
          <template v-slot:item.status="{ item }">
            <v-chip
              small
              label
              :color="isSettled(item) ? 'success' : 'warning'"
              text-color="white"
            >
              {{ isSettled(item) ? "Paid out" : "Pending" }}
            </v-chip>
          </template>

          <!-- Action Column -->
          <template v-slot:item.action="{ item }">
            <v-btn
              v-if="num(item.share_percentage) > 0"
              small
              depressed
              :color="isSettled(item) ? 'grey lighten-3' : 'primary'"
              @click="openTransactionDialog(item)"
            >
              <v-icon small left>
                {{ isSettled(item) ? "mdi-pencil" : "mdi-cash-check" }}
              </v-icon>
              {{ isSettled(item) ? "Edit" : "Enter Details" }}
            </v-btn>
          </template>

          <!-- Expanded Row Details -->
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="rsfl-td rsfl-td--expanded">
                    <div class="rsfl-expanded">
                      <div class="rsfl-section">
                        <h6 class="rsfl-section__title">Transaction Details</h6>

                        <div v-if="!isSettled(item)" class="rsfl-empty-txn">
                          <v-icon small color="warning" class="mr-2">
                            mdi-alert-circle-outline
                          </v-icon>
                          <span>
                            No payout recorded for this fortnight yet. Use
                            <strong>Enter Details</strong> to add the transaction.
                          </span>
                        </div>

                        <div v-else class="rsfl-details-grid">
                          <div class="rsfl-detail-item">
                            <span class="rsfl-detail__label">Transaction ID</span>
                            <span class="rsfl-detail__value">
                              {{ item.transaction_id || "—" }}
                            </span>
                          </div>
                          <div class="rsfl-detail-item">
                            <span class="rsfl-detail__label">Transaction Date</span>
                            <span class="rsfl-detail__value">
                              {{ item.transaction_date | formatDate }}
                            </span>
                          </div>
                          <div class="rsfl-detail-item">
                            <span class="rsfl-detail__label">Bank Name</span>
                            <span class="rsfl-detail__value">{{ item.bank_name || "—" }}</span>
                          </div>
                          <div class="rsfl-detail-item">
                            <span class="rsfl-detail__label">Bank Account</span>
                            <span class="rsfl-detail__value rsfl-detail__value--code">
                              {{ maskAccountNumber(item.bank_account_number) }}
                            </span>
                          </div>
                          <div class="rsfl-detail-item">
                            <span class="rsfl-detail__label">Payment Mode</span>
                            <span class="rsfl-detail__value">
                              {{ item.payment_mode || "—" }}
                            </span>
                          </div>
                          <div class="rsfl-detail-item">
                            <span class="rsfl-detail__label">Amount Paid</span>
                            <span class="rsfl-detail__value">
                              {{ currencyOrDash(item.paid_amount) }}
                            </span>
                          </div>
                          <div class="rsfl-detail-item">
                            <span class="rsfl-detail__label">Finalized On</span>
                            <span class="rsfl-detail__value">
                              {{ item.finalized_at | formatDateTime }}
                            </span>
                          </div>
                        </div>

                        <div v-if="item.remarks" class="rsfl-remarks">
                          <p class="rsfl-remarks__label">Remarks</p>
                          <p class="rsfl-remarks__text">{{ item.remarks }}</p>
                        </div>

                        <div v-if="amountsDiffer(item)" class="rsfl-empty-txn mt-3">
                          <v-icon small color="info" class="mr-2">mdi-information-outline</v-icon>
                          <span>
                            Paid out {{ currency(item.paid_amount) }}, but this fortnight
                            now totals {{ currency(item.total_amount) }} — invoices landed
                            after the payout.
                          </span>
                        </div>
                      </div>

                      <div class="rsfl-section">
                        <h6 class="rsfl-section__title">
                          Programme Breakdown
                          <span v-if="item.split_count">({{ item.split_count }} entries)</span>
                        </h6>

                        <div v-if="!item.split_summary.length" class="rsfl-empty-txn">
                          <v-icon small color="grey" class="mr-2">mdi-information-outline</v-icon>
                          <span>
                            The programme-wise breakdown is stored when the split is
                            finalized. {{ item.program_count }} programme(s) and
                            {{ item.invoice_count }} invoice(s) make up this fortnight.
                          </span>
                        </div>

                        <div v-else class="rsfl-table-wrap">
                          <table class="rsfl-table rsfl-table--inner">
                            <thead>
                              <tr>
                                <th class="rsfl-th">Program</th>
                                <th class="rsfl-th">Fee Type</th>
                                <th class="rsfl-th rsfl-th--num">Share %</th>
                                <th class="rsfl-th rsfl-th--num">Base Amount</th>
                                <th class="rsfl-th rsfl-th--num">GST %</th>
                                <th class="rsfl-th rsfl-th--num">GST Amount</th>
                                <th class="rsfl-th">GST Type</th>
                                <th class="rsfl-th rsfl-th--num">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(splitItem, sidx) in item.split_summary" :key="sidx">
                                <td class="rsfl-td">{{ splitItem.program_name }}</td>
                                <td class="rsfl-td">{{ splitItem.fee_type }}</td>
                                <td class="rsfl-td rsfl-td--num">
                                  {{ percent(splitItem.share_percentage) }}
                                </td>
                                <td class="rsfl-td rsfl-td--num">
                                  {{ currency(splitItem.base_amount) }}
                                </td>
                                <td class="rsfl-td rsfl-td--num">
                                  {{ percent(splitItem.gst_percentage) }}
                                </td>
                                <td class="rsfl-td rsfl-td--num">
                                  {{ currency(splitItem.gst_amount) }}
                                </td>
                                <td class="rsfl-td">{{ splitItem.split_type }}</td>
                                <td class="rsfl-td rsfl-td--num rsfl-td--strong">
                                  {{ currency(splitItem.total_amount) }}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
            </td>
          </template>
        </v-data-table>

        <!-- Footer with totals -->
        <div v-if="visiblePeriods.length" class="rsfl-footer-totals">
          <span class="rsfl-tf">TOTAL ({{ visiblePeriods.length }} fortnights)</span>
          <span class="rsfl-tf rsfl-tf--strong">{{ currency(visibleTotal) }}</span>
        </div>
      </div>
    </template>

    <!-- Empty State -->
    <v-card v-else outlined class="rsfl-state rsfl-state--card">
      <v-icon size="44" color="grey lighten-1">mdi-file-document-outline</v-icon>
      <h3 class="rsfl-state__title">No revenue found</h3>
      <p class="rsfl-state__text">
        This partner has no paid invoices, so there are no revenue splits to show.
      </p>
    </v-card>

    <!-- Shared Finalize dialog (Details / Review / Confirm) -->
    <RevenueSplitTransactionDialog
      v-model="showTransactionDialog"
      :context="dialogContext"
      @saved="loadPeriods"
    />
  </div>
</template>

<script>
import RevenueSplitTransactionDialog from "@/components/revenueSplit/TransactionDialog.vue";
import {
  currency,
  maskAccountNumber,
  monthName,
  num,
  percent,
  periodLabel,
} from "@/components/revenueSplit/transaction";

export default {
  name: "RevenueSplitFinalizedList",

  components: { RevenueSplitTransactionDialog },

  filters: {
    formatDate(value) {
      if (!value) return "—";
      return new Date(value).toLocaleDateString("en-IN");
    },
    formatDateTime(value) {
      if (!value) return "—";
      return new Date(value).toLocaleString("en-IN");
    },
  },

  data() {
    return {
      loading: false,
      loadingPartners: false,
      periods: [],
      partners: [],
      // What the user is editing vs what the shown result set was built from —
      // the status filter only takes effect on Search, like the partner does.
      form: { partner_id: null, status: "all" },
      applied: { partner_id: null, status: "all" },
      statusOptions: [
        { value: "all", text: "All periods" },
        { value: "pending", text: "Pending" },
        { value: "settled", text: "Paid out" },
      ],
      expandedItems: [],
      showTransactionDialog: false,
      selectedPeriod: null,
    };
  },

  computed: {
    partnerName() {
      const partner = this.partners.find((p) => p.id === this.applied.partner_id);
      return partner ? partner.name : "Partner";
    },

    visiblePeriods() {
      if (this.applied.status === "all") return this.periods;
      return this.periods.filter((p) => p.status === this.applied.status);
    },

    periodsWithKey() {
      return this.visiblePeriods.map((p) => ({
        ...p,
        _key: this.periodKey(p),
      }));
    },

    visibleTotal() {
      return this.visiblePeriods.reduce((sum, p) => sum + num(p.total_amount), 0);
    },

    totals() {
      return this.periods.reduce(
        (acc, period) => {
          const amount = num(period.total_amount);
          acc.revenue += amount;
          if (this.isSettled(period)) {
            acc.settled += amount;
          } else {
            acc.pending += amount;
            acc.pendingCount += 1;
          }
          return acc;
        },
        { revenue: 0, settled: 0, pending: 0, pendingCount: 0 }
      );
    },

    dialogContext() {
      const period = this.selectedPeriod || {};
      return {
        historyId: period.id || null,
        partnerId: this.applied.partner_id,
        partnerName: this.partnerName,
        month: period.month,
        year: period.year,
        fortnight: period.fortnight,
        periodLabel: periodLabel(period.month, period.year, period.fortnight),
        totalRevenue: num(period.total_amount),
        totalPartnerShare: this.hasShare(period) ? num(period.partner_share) : null,
        transaction: period.id
          ? {
              transaction_id: period.transaction_id || "",
              transaction_date: this.toInputDate(period.transaction_date),
              utr_number: period.utr_number || "",
              bank_name: period.bank_name || "",
              bank_account_number: period.bank_account_number || "",
              payment_mode: period.payment_mode || "",
              remarks: period.remarks || "",
            }
          : null,
      };
    },

    tableHeaders() {
      return [
        { text: "Period", value: "period", align: "left", width: "150px" },
        { text: "Share %", value: "share_percentage", align: "right", width: "80px" },
        { text: "Base Amount", value: "base_amount", align: "right", width: "120px" },
        { text: "GST %", value: "gst_percentage", align: "right", width: "80px" },
        { text: "GST Amount", value: "gst_amount", align: "right", width: "120px" },
        { text: "GST Type", value: "gst_type", align: "left", width: "100px" },
        { text: "Total", value: "partner_share", align: "right", width: "120px" },
        { text: "Status", value: "status", align: "left", width: "100px" },
        { text: "", value: "action", align: "center", width: "150px", sortable: false },
      ];
    },
  },

  created() {
    this.setBreadcrumbs();
    this.initialise();
  },

  methods: {
    currency,
    maskAccountNumber,
    monthName,
    num,
    percent,

    setBreadcrumbs() {
      const crumbs = [
        { text: "Partner Revenue Split", to: { name: "Partner Revenue Split" } },
      ];
      // Deep links from the summary page carry the partner id, so the
      // middle crumb can point back to that partner's split summary.
      const partnerId = this.$route.query.partner_id;
      if (partnerId) {
        crumbs.push({
          text: "Split Summary",
          to: { name: "RevenueSplitSummary", params: { id: partnerId } },
        });
      }
      crumbs.push({ text: "Finalized Records" });
      this.$store.commit("setBreadcrumbs", crumbs);
    },

    async initialise() {
      await this.loadPartners();
      // Deep links from the summary page land here with a partner already chosen.
      const routePartner = this.$route.query.partner_id;
      if (routePartner) {
        this.form.partner_id = Number(routePartner);
        await this.search();
      }
    },

    async loadPartners() {
      this.loadingPartners = true;
      try {
        const { data } = await this.$axios.get("/partners");
        this.partners = data.data || [];
      } catch (err) {
        this.notifyError(err, "Unable to load partners");
      } finally {
        this.loadingPartners = false;
      }
    },

    async search() {
      if (!this.form.partner_id) return;
      const partnerChanged = this.applied.partner_id !== this.form.partner_id;
      this.applied = { ...this.form };
      if (partnerChanged) this.expandedItems = [];
      await this.loadPeriods();
    },

    async loadPeriods() {
      if (!this.applied.partner_id) {
        this.periods = [];
        return;
      }

      this.loading = true;
      try {
        const params = new URLSearchParams({ partner_id: this.applied.partner_id });
        const { data } = await this.$axios.get(
          `/revenue_split_histories_finalized?${params}`
        );
        this.periods = (data.data || []).map((period) => ({
          ...period,
          split_summary: period.split_summary || [],
        }));
      } catch (err) {
        this.notifyError(err, "Unable to load revenue splits");
        this.periods = [];
      } finally {
        this.loading = false;
      }
    },

    isSettled(period) {
      return period && period.status === "settled";
    },

    hasShare(period) {
      return period.partner_share !== null && period.partner_share !== undefined;
    },

    periodKey(period) {
      return `${period.year}-${period.month}-${period.fortnight || 0}`;
    },

    periodTitle(period) {
      const fn = period.fortnight ? ` - FN${period.fortnight}` : "";
      return `${monthName(period.month)} ${period.year}${fn}`;
    },

    isExpanded(period) {
      return this.expandedItems.includes(this.periodKey(period));
    },

    toggleExpand(period) {
      const key = this.periodKey(period);
      const idx = this.expandedItems.indexOf(key);
      if (idx > -1) this.expandedItems.splice(idx, 1);
      else this.expandedItems.push(key);
    },

    currencyOrDash(value) {
      if (value === null || value === undefined || value === "") return "—";
      return currency(value);
    },

    percentOrDash(value) {
      if (value === null || value === undefined || value === "") return "—";
      return percent(value);
    },

    // The payout amount is frozen at finalization; invoices that land later make
    // the fortnight total drift, and finance needs to see that.
    amountsDiffer(period) {
      if (period.paid_amount === null || period.paid_amount === undefined) return false;
      return num(period.paid_amount).toFixed(2) !== num(period.total_amount).toFixed(2);
    },

    toInputDate(value) {
      if (!value) return "";
      return String(value).slice(0, 10);
    },

    openTransactionDialog(period) {
      this.selectedPeriod = period;
      this.showTransactionDialog = true;
    },

    notifyError(err, fallback) {
      this.$store.commit("showSnackbar", {
        message: err?.response?.data?.message || err?.response?.data?.error || fallback,
        color: "error",
      });
    },
  },
};
</script>

<style scoped>
.rsfl-page {
  --rsfl-primary: var(--v-primary-base, #00aff1);
  --rsfl-line: #e4e7ec;
  --rsfl-ink: #101828;
  --rsfl-ink-2: #475467;
  --rsfl-ink-3: #98a2b3;
  padding: 16px 20px 32px;
  min-height: 100%;
}

/* Header */
.rsfl-header {
  margin-bottom: 24px;
}

.rsfl-header__title {
  margin: 0 0 4px;
  font-size: 24px;
  font-weight: 700;
  color: var(--rsfl-ink);
}

.rsfl-header__subtitle {
  margin: 0;
  font-size: 13px;
  color: var(--rsfl-ink-2);
}

/* Filters */
.rsfl-filters {
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  margin-bottom: 16px;
}

.rsfl-filters__inner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
}

.rsfl-filters__field {
  flex: 0 1 240px;
}

/* Section */
.rsfl-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.rsfl-section__title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--rsfl-ink);
}

/* Details Grid */
.rsfl-details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.rsfl-detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.rsfl-detail__label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--rsfl-ink-2);
}

.rsfl-detail__value {
  font-size: 13px;
  font-weight: 500;
  color: var(--rsfl-ink);
}

.rsfl-detail__value--code {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  font-weight: 600;
  color: #1565c0;
}

.rsfl-detail__value--utr {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  font-weight: 700;
  color: #2e7d32;
  font-variant-numeric: tabular-nums;
}

/* Remarks */
.rsfl-remarks {
  padding: 12px;
  background: #f5f5f5;
  border-radius: 6px;
  border-left: 3px solid var(--rsfl-primary);
}

.rsfl-remarks__label {
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--rsfl-ink-2);
}

.rsfl-remarks__text {
  margin: 0;
  font-size: 12px;
  color: var(--rsfl-ink);
  line-height: 1.5;
}

/* Table */
.rsfl-table-wrap {
  overflow-x: auto;
}

.rsfl-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.rsfl-th {
  padding: 10px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--rsfl-ink-2);
  background: #f9fafb;
  border-bottom: 1px solid var(--rsfl-line);
  white-space: nowrap;
}

.rsfl-th--num {
  text-align: right;
}

.rsfl-td {
  padding: 10px;
  font-size: 12px;
  color: var(--rsfl-ink-2);
  border-bottom: 1px solid #f4f5f7;
}

.rsfl-td--num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.rsfl-td--strong {
  font-weight: 700;
  color: var(--rsfl-ink);
}

.rsfl-tf {
  padding: 10px;
  font-size: 12px;
  font-weight: 700;
  color: var(--rsfl-ink);
  background: #f9fafb;
  border-top: 1px solid var(--rsfl-line);
}

.rsfl-tf--strong {
  font-weight: 700;
}

/* States */
.rsfl-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 56px 20px;
}

.rsfl-state--card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.rsfl-state__text {
  margin: 12px 0 0;
  font-size: 13px;
  color: var(--rsfl-ink-2);
}

.rsfl-state__title {
  margin: 14px 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: var(--rsfl-ink);
}

/* Details Section */
.rsfl-details-section {
  margin-top: 24px;
}

/* Data Table */
.rsfl-data-table {
  border: 1px solid var(--rsfl-line);
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.rsfl-data-table ::v-deep .v-data-table__wrapper {
  border-radius: 10px;
}

.rsfl-data-table ::v-deep .v-data-table th {
  background: #f9fafb !important;
  font-weight: 700 !important;
  font-size: 11px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.6px !important;
  color: var(--rsfl-ink-2) !important;
}

.rsfl-data-table ::v-deep .v-data-table td {
  font-size: 12px !important;
  color: var(--rsfl-ink-2) !important;
}

.rsfl-data-table ::v-deep .v-data-table tbody tr:hover {
  background: #f5f5f5 !important;
}

/* Footer Totals */
.rsfl-footer-totals {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f9fafb;
  border: 1px solid var(--rsfl-line);
  border-top: none;
  border-radius: 0 0 10px 10px;
  font-weight: 700;
  color: var(--rsfl-ink);
  font-size: 13px;
}

.rsfl-details-title {
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 700;
  color: var(--rsfl-ink);
}

@media (max-width: 768px) {
  .rsfl-details-grid {
    grid-template-columns: 1fr;
  }
}

/* Totals strip */
.rsfl-totals {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.rsfl-total {
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  padding: 12px 14px;
}

.rsfl-total__label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #667085;
}

.rsfl-total__value {
  font-size: 18px;
  font-weight: 600;
  color: #101828;
}

.rsfl-total__value--success {
  color: #12805c;
}

.rsfl-total__value--warning {
  color: #b54708;
}

/* Placeholder shown where a payout or breakdown does not exist yet */
.rsfl-empty-txn {
  display: flex;
  align-items: flex-start;
  font-size: 13px;
  color: #475467;
  background: #f9fafb;
  border: 1px dashed #d0d5dd;
  border-radius: 6px;
  padding: 10px 12px;
}
/* Fortnight table */
.rsfl-th--period {
  min-width: 190px;
}

.rsfl-th--action,
.rsfl-td--action {
  text-align: right;
  white-space: nowrap;
}

.rsfl-row:hover {
  background: #f9fafb;
}

.rsfl-td--period {
  white-space: nowrap;
}

.rsfl-row__chevron {
  cursor: pointer;
  margin-right: 6px;
  transition: transform 0.15s ease;
}

.rsfl-row__chevron--open {
  transform: rotate(90deg);
}

.rsfl-row__period {
  font-weight: 600;
  color: #101828;
}

.rsfl-td--expanded {
  background: #f9fafb;
  padding: 16px 20px !important;
}

.rsfl-expanded {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.rsfl-table--inner {
  background: #fff;
}

.rsfl-details-title__count {
  font-size: 12px;
  font-weight: 500;
  color: #98a2b3;
}
</style>

